import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { SpeakerHigh, SpeakerSlash, Play, ArrowsClockwise, Lightbulb, Star, House } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Plant } from "./Plant";
import { WateringCan } from "./WateringCan";
import { WaterDrop } from "./WaterDrop";
// Reuse existing Bloom audio + celebration patterns (read-only imports).
import { useNarration } from "../addition/useNarration";
import { sfx, unlockAudio, setSoundMuted } from "../addition/sound";
import { Confetti } from "../addition/Confetti";
import { plantElement } from "../../home/worldStore";

const NUM = ["zero", "one", "two", "three"];
// Narration split into short phrases so the voice never gets cut off.
const LINES = {
  intro: ["This plant is thirsty."],
  ask: ["Can you give the plant three drops of water?", "Drag the water drops into the pot."],
  miss: ["Let's try again.", "The plant needs three drops."],
  help: ["Drag a water drop", "into the plant pot."],
  praise: ["Amazing!", "The plant is growing!"],
};

export function WaterStory() {
  const reduce = useReducedMotion();
  const { speak, cancel } = useNarration();

  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState("start"); // start | ask | grown
  const [placed, setPlaced] = useState([false, false, false]);
  const [mood, setMood] = useState("happy");
  const [muted, setMuted] = useState(false);
  const [caption, setCaption] = useState("");
  const [progress, setProgress] = useState(20);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showXp, setShowXp] = useState(false);
  const [hintGlow, setHintGlow] = useState(false);

  const placedCount = placed.filter(Boolean).length;

  const timers = useRef([]);
  const lastLine = useRef("");
  const mutedRef = useRef(false);
  const placedRef = useRef(0);
  const potRef = useRef(null);

  const after = useCallback((ms, fn) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);
  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  useEffect(() => {
    mutedRef.current = muted;
    setSoundMuted(muted);
    if (muted) cancel();
  }, [muted, cancel]);

  useEffect(() => () => { clearTimers(); cancel(); }, [clearTimers, cancel]);

  const say = useCallback((text, opts) => {
    lastLine.current = text;
    setCaption(Array.isArray(text) ? text.join(" ") : text);
    if (!mutedRef.current) speak(text, opts);
  }, [speak]);

  const askNow = useCallback(() => {
    setPhase("ask");
    setMood("idle");
    say(LINES.ask);
  }, [say]);

  const start = useCallback(() => {
    unlockAudio();
    setStarted(true);
    setMood("happy");
    say(LINES.intro);
    after(2300, askNow);
  }, [after, say, askNow]);

  const insidePot = useCallback((pt) => {
    const el = potRef.current;
    if (!el || !pt) return false;
    const r = el.getBoundingClientRect();
    const m = 44; // generous hit area for small hands
    return pt.x >= r.left - m && pt.x <= r.right + m && pt.y >= r.top - m && pt.y <= r.bottom + m;
  }, []);

  const celebrate = useCallback(() => {
    setMood("celebrating");
    setShowConfetti(!reduce);
    setShowXp(true);
    setProgress(100);
    setPhase("grown");
    if (!mutedRef.current) sfx.success();
    say(LINES.praise);
    plantElement({ from: "water-the-plant", label: "You grew this by giving the plant three drops!" });
  }, [reduce, say]);

  const handleDrop = useCallback((i, info) => {
    if (phase !== "ask" || placed[i]) return;
    if (!insidePot(info.point)) {
      setMood("confused");
      if (!mutedRef.current) sfx.tryAgain();
      say(LINES.miss);
      after(1500, () => setMood("idle"));
      return; // drop snaps back to the tray
    }
    placedRef.current += 1;
    const count = placedRef.current;
    setPlaced((prev) => { const n = [...prev]; n[i] = true; return n; });
    setHintGlow(false);
    setProgress(20 + Math.round((count * 80) / 3));
    if (!mutedRef.current) sfx.pop();
    if (count >= 3) {
      setMood("happy");
      after(450, celebrate);
    } else {
      setMood("happy");
      say([`${NUM[count]} ${count === 1 ? "drop" : "drops"}!`]);
      after(1000, () => setMood("idle"));
    }
  }, [phase, placed, insidePot, after, say, celebrate]);

  const help = useCallback(() => {
    if (phase !== "ask") return;
    unlockAudio();
    setHintGlow(true);
    setMood("confused");
    say(LINES.help);
    after(1600, () => setMood("idle"));
  }, [phase, after, say]);

  const replay = useCallback(() => {
    unlockAudio();
    say(lastLine.current || LINES.ask);
  }, [say]);

  const playAgain = useCallback(() => {
    clearTimers();
    placedRef.current = 0;
    setPlaced([false, false, false]);
    setShowConfetti(false);
    setShowXp(false);
    setHintGlow(false);
    setProgress(20);
    setMood("happy");
    setPhase("ask");
    after(150, () => say(LINES.ask));
  }, [after, say, clearTimers]);

  return (
    <div className="relative flex h-[100svh] w-full flex-col overflow-hidden bg-gradient-to-b from-[#FFF7EF] via-[#FFF1E6] to-[#DCF3EC] dark:from-[#171C28] dark:via-[#1B2030] dark:to-[#142420]">
      {/* decorative sky */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute right-6 top-6 h-24 w-24 rounded-full bg-secondary/70 blur-[2px] dark:bg-secondary/40" />
        <div className="absolute right-4 top-4 h-28 w-28 rounded-full bg-secondary/30 blur-2xl" />
        {!reduce && (
          <>
            <motion.div className="absolute left-[12%] top-[12%] h-7 w-20 rounded-full bg-white/80 dark:bg-white/10" animate={{ x: [0, 18, 0] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }} />
            <motion.div className="absolute left-[58%] top-[8%] h-6 w-16 rounded-full bg-white/70 dark:bg-white/10" animate={{ x: [0, -16, 0] }} transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }} />
          </>
        )}
        <div className="absolute -bottom-28 left-1/2 h-[55%] w-[170%] -translate-x-1/2 rounded-[50%] bg-gradient-to-b from-teal/30 to-teal/10 dark:from-teal/15 dark:to-transparent" />
      </div>

      {/* TOP BAR */}
      <div className="relative z-30 flex shrink-0 items-center justify-between px-4 py-2.5 sm:px-6">
        <div className="flex items-center gap-2">
          <Link to="/home/year1" aria-label="Back to your world" data-testid="exit-home" className="flex h-11 w-11 items-center justify-center rounded-full bg-card/80 text-foreground shadow-soft backdrop-blur transition hover:bg-card">
            <House size={20} weight="bold" />
          </Link>
          <span className="rounded-full bg-card/80 px-3 py-1.5 text-sm font-bold text-foreground shadow-soft backdrop-blur">Water the Plant</span>
        </div>
        <button
          onClick={() => setMuted((m) => !m)}
          aria-label={muted ? "Turn sound on" : "Turn sound off"}
          data-testid="mute-toggle"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-card/80 text-foreground shadow-soft backdrop-blur transition hover:bg-card"
        >
          {muted ? <SpeakerSlash size={20} weight="bold" /> : <SpeakerHigh size={20} weight="fill" />}
        </button>
      </div>

      {/* PROGRESS */}
      <div className="relative z-30 shrink-0 px-4 sm:px-6">
        <div className="mx-auto flex max-w-xl items-center gap-3">
          <div className="h-3.5 flex-1 overflow-hidden rounded-full bg-card/70 shadow-inner backdrop-blur">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-primary to-secondary" animate={{ width: `${progress}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} />
          </div>
          <AnimatePresence>
            {showXp && (
              <motion.span initial={{ scale: 0, y: 6 }} animate={{ scale: 1, y: 0 }} className="inline-flex items-center gap-1 rounded-full bg-secondary/30 px-2.5 py-1 text-sm font-black text-secondary-ink shadow-soft">
                <Star size={14} weight="fill" /> +5
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* QUESTION / NARRATION BUBBLE */}
      <div className="relative z-30 mt-2 flex min-h-[52px] shrink-0 items-center justify-center px-4">
        <AnimatePresence mode="wait">
          {started && caption && (
            <motion.div
              key={caption}
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex max-w-[92%] items-center gap-2 rounded-full bg-card px-4 py-2 text-center shadow-soft"
            >
              <SpeakerHigh size={16} weight="fill" className="flex-shrink-0 text-primary" />
              <span data-testid="narration-caption" className="text-sm font-bold text-foreground sm:text-base">{caption}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* SCENE */}
      <div className="relative z-10 min-h-0 w-full flex-1">
        {showConfetti && <Confetti />}

        {/* three empty drop positions (target counter) */}
        <div className="absolute left-1/2 top-2 z-10 flex -translate-x-1/2 gap-3">
          {[0, 1, 2].map((i) => (
            <motion.div key={i} animate={i < placedCount ? { scale: [1, 1.25, 1] } : {}} transition={{ duration: 0.4 }}>
              <WaterDrop empty={i >= placedCount} className="h-7 w-auto sm:h-8" />
            </motion.div>
          ))}
        </div>

        {/* plant (drop target) + watering can */}
        <div className="absolute inset-x-0 bottom-1 z-0 flex items-end justify-center gap-4 sm:gap-12">
          <motion.div
            ref={potRef}
            data-testid="pot-dropzone"
            className="relative"
            animate={reduce ? {} : mood === "celebrating" ? { y: [0, -16, 0, -8, 0] } : mood === "confused" ? { rotate: [0, -3, 3, -2, 0] } : { y: [0, -5, 0] }}
            transition={reduce ? {} : mood === "celebrating" ? { duration: 0.9, repeat: 2 } : mood === "confused" ? { duration: 0.6 } : { duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <Plant growth={placedCount} mood={mood} className="h-[182px] w-auto sm:h-[212px]" />
          </motion.div>
          <motion.div className="mb-3" animate={reduce ? {} : { rotate: [0, -4, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
            <WateringCan className="h-[84px] w-auto sm:h-[100px]" />
          </motion.div>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="relative z-30 shrink-0 px-4 pb-4 pt-1 sm:pb-5">
        <div className="mx-auto w-full max-w-xl">
          {/* draggable water tray */}
          {phase === "ask" && (
            <div className="mb-3 flex min-h-[64px] items-center justify-center gap-6">
              {[0, 1, 2].map((i) =>
                !placed[i] ? (
                  <motion.div
                    key={i}
                    drag
                    dragSnapToOrigin
                    dragElastic={0.25}
                    whileDrag={{ scale: 1.25, zIndex: 60 }}
                    onDragStart={() => { unlockAudio(); if (!mutedRef.current) sfx.tap(); }}
                    onDragEnd={(e, info) => handleDrop(i, info)}
                    animate={hintGlow && !reduce ? { y: [0, -9, 0] } : { y: 0 }}
                    transition={hintGlow ? { duration: 1, repeat: Infinity } : { type: "spring", stiffness: 300, damping: 18 }}
                    data-testid={`water-drop-${i}`}
                    className="cursor-grab touch-none active:cursor-grabbing"
                    style={{ touchAction: "none" }}
                  >
                    <WaterDrop className="h-12 w-auto drop-shadow-md sm:h-14" />
                  </motion.div>
                ) : null,
              )}
            </div>
          )}

          {/* grown — calm celebration + replay */}
          {phase === "grown" && (
            <motion.div initial={{ opacity: 0, scale: 0.92, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="mx-auto max-w-md rounded-[1.75rem] border border-border bg-card/90 p-4 text-center shadow-soft-lg backdrop-blur">
              <div className="flex items-center justify-center gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.1 + i * 0.12, type: "spring", stiffness: 400, damping: 12 }}>
                    <Star size={i === 1 ? 40 : 30} weight="fill" className="text-secondary" />
                  </motion.span>
                ))}
              </div>
              <p className="mt-1 font-display text-2xl font-black">The plant is growing!</p>
              <p className="text-sm font-semibold text-muted-foreground">Amazing — you gave it three drops!</p>
              <button
                onClick={playAgain}
                data-testid="play-again"
                className="mt-3 inline-flex min-h-[52px] items-center gap-2 rounded-full bg-primary px-7 font-display text-lg font-extrabold text-primary-foreground shadow-glow transition-all duration-200 ease-bounce hover:-translate-y-0.5 active:scale-95"
              >
                <ArrowsClockwise size={20} weight="bold" /> Play again
              </button>
            </motion.div>
          )}

          {/* persistent audio + help controls */}
          {started && phase !== "grown" && (
            <div className="mt-1 flex items-center justify-center gap-3">
              <button
                onClick={replay}
                data-testid="replay-button"
                className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-primary px-6 font-display text-base font-extrabold text-primary-foreground shadow-glow transition-all duration-200 ease-bounce hover:-translate-y-0.5 active:scale-95 sm:text-lg"
              >
                <SpeakerHigh size={22} weight="fill" /> Listen Again
              </button>
              <button
                onClick={help}
                disabled={phase !== "ask"}
                data-testid="help-button"
                className="inline-flex min-h-[52px] items-center gap-2 rounded-full border-2 border-border bg-card px-5 font-display text-base font-bold text-foreground shadow-soft transition-all hover:border-primary hover:text-primary-strong active:scale-95 disabled:opacity-50"
              >
                <Lightbulb size={20} weight="fill" /> Help
              </button>
            </div>
          )}
        </div>
      </div>

      {/* START SPLASH */}
      <AnimatePresence>
        {!started && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b from-[#FFF7EF] via-[#FFF1E6] to-[#DCF3EC] px-6 text-center dark:from-[#171C28] dark:via-[#1B2030] dark:to-[#142420]"
          >
            <motion.div animate={reduce ? {} : { y: [0, -10, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}>
              <Plant growth={1} mood="happy" className="h-[190px] w-auto" />
            </motion.div>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-foreground">Water the Plant</h1>
            <p className="mt-2 max-w-xs font-semibold text-muted-foreground">This little plant is thirsty. Help it grow!</p>
            <button
              onClick={start}
              data-testid="start-button"
              className="mt-7 inline-flex min-h-[64px] items-center gap-3 rounded-full bg-primary px-10 font-display text-xl font-black text-primary-foreground shadow-glow transition-all duration-200 ease-bounce hover:-translate-y-1 active:scale-95"
            >
              <Play size={26} weight="fill" /> Tap to Play
            </button>
            <p className="mt-4 flex items-center gap-1.5 text-sm font-bold text-muted-foreground">
              <SpeakerHigh size={16} weight="fill" /> Sound on for the best experience
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default WaterStory;
